import { useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/components/ui/table';
import { Button } from 'src/components/ui/button';
import { InvoiceGroupPreviewItem } from '../../../redux/billing/billing.types';
import { Company } from 'src/pages/billing/types/billing.types';
import { useCreateMappingMutation, useCreateCampaignMappingMutation } from '../../../redux/billing/billing.endpoints';
import { RefreshCw } from "lucide-react";
import Select from 'react-select';

interface SearchableCompanyDropdownProps {
  companies: Array<{ id: string; name: string }>;
  value: string;
  onChange: (companyName: string) => void;
}

const SearchableCompanyDropdown = ({ companies, value, onChange }: SearchableCompanyDropdownProps) => {
  const options = companies.map(company => ({
    label: company.name,
    value: company.name,
  }));

  const selectedOption = options.find(option => option.value === value) || null;

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: '40px',
      fontSize: '14px',
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.1)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 50,
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#9ca3af',
    }),
  };

  return (
    <Select
      options={options}
      value={selectedOption}
      onChange={(selectedOption) => onChange(selectedOption?.value || '')}
      placeholder="Select Company"
      isSearchable={true}
      isClearable={false}
      styles={customStyles}
      className="w-full min-w-[180px]"
    />
  );
};

interface InvoiceGroupPreviewTableProps {
  previewData: InvoiceGroupPreviewItem[];
  companies: Company[];
  onRefresh?: () => void;
}

export const InvoiceGroupPreviewTable = ({ previewData, companies, onRefresh }: InvoiceGroupPreviewTableProps) => {
  const [createMapping] = useCreateMappingMutation();
  const [createCampaignMapping] = useCreateCampaignMappingMutation();

  const [localCompanySelections, setLocalCompanySelections] = useState<Record<string, string>>({});
  const [activeFilter, setActiveFilter] = useState<'unassigned' | 'all'>('all');

  const sortedCompanies = useMemo(() => {
    return companies
      .map(company => ({
        id: company.id,
        name: company.company_name || company.id,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [companies]);

  const filteredData = useMemo(() => {
    if (activeFilter === 'all') {
      return previewData;
    }
    return previewData.filter(item => !item.company || item.company === 'Unassigned');
  }, [previewData, activeFilter]);

  const getCompanyIdByName = (companyName: string | null): string | null => {
    if (!companyName) return null;
    const company = sortedCompanies.find(c => c.name === companyName);
    return company?.id || null;
  };

  const handleCompanyChange = async(item: InvoiceGroupPreviewItem, companyName: string) => {
    if (!companyName) return;

    // Update local state immediately for visual feedback
    const itemKey = `${item.advertiser_name}|${item.campaign_name}`;
    setLocalCompanySelections(prev => ({
      ...prev,
      [itemKey]: companyName,
    }));

    const companyId = getCompanyIdByName(companyName);
    if (!companyId) return;

    try {
      // Create both advertiser and campaign mappings immediately
      await Promise.all([
        createMapping({
          advertiser_campaign_name: item.advertiser_name,
          company: companyId,
        }).unwrap(),
        createCampaignMapping({
          advertiser_campaign_name: item.campaign_name,
          company: companyId,
        }).unwrap(),
      ]);
    } catch (error) {
      console.error('Failed to create mappings:', error);
      // Revert local state on error
      setLocalCompanySelections(prev => {
        const newState = { ...prev };
        delete newState[itemKey];
        return newState;
      });
    }
  };

  const getCurrentCompanySelection = (item: InvoiceGroupPreviewItem): string => {
    const itemKey = `${item.advertiser_name}|${item.campaign_name}`;
    return localCompanySelections[itemKey] || item.company || '';
  };

  const renderFilterButton = (filter: typeof activeFilter, label: string) => (
    <Button
      size='med'
      variant={activeFilter === filter ? 'primary' : 'outline'}
      onClick={() => setActiveFilter(filter)}
      key={filter}
      className={activeFilter === filter ? 'shadow-md hover:shadow-lg transition-all duration-200' : ''}
    >
      {label}
    </Button>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-row gap-2 justify-start">
        {renderFilterButton('all', 'All')}
        {renderFilterButton('unassigned', 'Unassigned')}
        <Button
          size='med'
          variant='outline'
          onClick={onRefresh}
          className='justify-end ml-auto'
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="max-h-[60vh] overflow-y-auto">
          <Table className="min-w-full">
            <TableHeader className="sticky top-0 bg-gray-50 z-10">
              <TableRow>
                <TableHead className="text-gray-600 py-3 text-left">
                  Advertiser Name
                </TableHead>
                <TableHead className="text-gray-600 py-3 text-left">
                  Campaign Name
                </TableHead>
                <TableHead className="text-gray-600 py-3 text-left">
                  Platform
                </TableHead>
                <TableHead className="text-gray-600 py-3 text-left">
                  Company
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="py-3 text-left">
                    <div className="font-medium text-gray-900 max-w-[200px] truncate">
                      {item.advertiser_name}
                    </div>
                  </TableCell>
                  <TableCell className="py-3 text-left">
                    <div
                      className="text-gray-700 max-w-[250px] truncate"
                      title={item.campaign_name}
                    >
                      {item.campaign_name}
                    </div>
                  </TableCell>
                  <TableCell className="py-3 text-left">
                    <div className="text-gray-600 capitalize">
                      {item.platform}
                    </div>
                  </TableCell>
                  <TableCell className="py-3 text-left">
                    <SearchableCompanyDropdown
                      companies={sortedCompanies}
                      value={getCurrentCompanySelection(item)}
                      onChange={(companyName) => handleCompanyChange(item, companyName)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default InvoiceGroupPreviewTable;